import { useCallback, useEffect, useMemo, useState } from "react";
import { useUrlState } from "@/lib/navState";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Plate } from "@/components/StatusBadge";
import { label } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Recycle, Search } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";

const STAGES = ["EXPECTED_SALVAGE", "REMOVED_FROM_VEHICLE", "SALVAGE_RECEIVED", "QC", "DISPOSITION"];
const OWNERSHIP = ["TITANIUM", "INSURANCE", "CUSTOMER", "PENDING_CONFIRMATION"];
const QC = ["PENDING_QC", "READY", "REPAIR", "REJECT", "SCRAP", "RETURN_TO_INSURANCE", "RETURN_TO_CUSTOMER"];

const FILTERS = [
  ["ALL", "All"], ["EXPECTED", "Expected"], ["RECEIVED", "Received"], ["QC", "QC"],
  ["READY_GALLERY", "Ready Gallery"], ["REPAIR", "Repair"], ["RETURN", "Return"], ["REJECT", "Reject"],
];

function matchFilter(s, f) {
  if (f === "ALL") return true;
  if (f === "EXPECTED") return s.stage === "EXPECTED_SALVAGE";
  if (f === "RECEIVED") return s.stage === "SALVAGE_RECEIVED" || s.stage === "REMOVED_FROM_VEHICLE";
  if (f === "QC") return s.stage === "QC" || s.qc_status === "PENDING_QC";
  if (f === "READY_GALLERY") return s.ownership_status === "TITANIUM" && s.qc_status === "READY";
  if (f === "REPAIR") return s.qc_status === "REPAIR";
  if (f === "RETURN") return s.qc_status === "RETURN_TO_INSURANCE" || s.qc_status === "RETURN_TO_CUSTOMER";
  if (f === "REJECT") return s.qc_status === "REJECT" || s.qc_status === "SCRAP";
  return true;
}

function Sel({ value, onChange, options, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="mt-1 h-9 text-xs"><SelectValue /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o} value={o} className="text-xs">{label(o)}</SelectItem>)}</SelectContent>
    </Select>
  );
}

export default function WarehouseSalvage() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("ALL");
  const [q, setQ] = useUrlState("q", "");
  const navigate = useNavigate();
  const load = useCallback(async () => { try { const { data } = await api.get("/salvage"); setRows(data || []); } catch (e) { /* ignore */ } }, []);
  useEffect(() => { load(); }, [load]);
  const patch = async (id, body) => {
    try { await api.patch(`/salvage/${id}`, body); toast.success("Salvage updated"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const visible = useMemo(() => rows.filter((s) => matchFilter(s, filter)).filter((s) => {
    if (!q) return true; const t = q.toLowerCase();
    return (s.license_plate || "").toLowerCase().includes(t) || (s.old_part_name || "").toLowerCase().includes(t);
  }), [rows, filter, q]);

  return (
    <div className="ti-dark -m-3 -mb-24 min-h-[calc(100vh-3rem)] px-4 py-4 sm:-m-5 sm:px-6 lg:-mb-5 lg:px-8 lg:py-6">
      <h1 className="font-heading text-xl font-bold text-[#111827] sm:text-2xl"><Recycle size={26} className="mr-2 inline text-[#3B82F6]" />Salvage Warehouse</h1>
      <p className="mt-1 text-sm text-gray-500">Old parts removed during repair · Ownership TITANIUM + QC READY → Pernah Baru Gallery</p>
      <div className="relative mt-3">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input data-testid="salvage-search" placeholder="Search plate or old part…" value={q} onChange={(e) => setQ(e.target.value)} className="h-10 pl-10" />
      </div>
      <div className="-mx-1 mt-3 flex gap-1.5 overflow-x-auto px-1 pb-1">
        {FILTERS.map(([k, l]) => (
          <button key={k} data-testid={`salvage-filter-${k}`} onClick={() => setFilter(k)} className={`whitespace-nowrap rounded-full border px-3 py-1 text-xs font-medium transition-colors ${filter === k ? "border-[#111827] bg-[#111827] text-white" : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"}`}>{l}</button>
        ))}
      </div>
      <div className="mt-3 space-y-2">
        {visible.map((s) => (
          <div key={s.id} data-testid={`salvage-${s.id}`} className="rounded-lg border border-gray-100 bg-white p-3 shadow-sm">
            <div className="flex items-center gap-2">
              <button onClick={() => navigate(`/claims/${s.claim_id}`)} className="shrink-0"><Plate value={s.license_plate} /></button>
              <span className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-800">{s.old_part_name}</span>
              {s.gallery_id && <span data-testid={`salvage-gallery-${s.id}`} className="shrink-0 rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-semibold text-white">IN GALLERY</span>}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-gray-500">
              <span>No: {s.part_number || "-"}</span><span>· {s.vehicle || "-"}</span><span>· {s.insurance_company || "-"}</span>
            </div>
            <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
              <div><span className="text-[10px] uppercase tracking-wide text-gray-400">Stage</span><Sel testid={`salvage-stage-${s.id}`} value={s.stage} onChange={(v) => patch(s.id, { stage: v })} options={STAGES} /></div>
              <div><span className="text-[10px] uppercase tracking-wide text-gray-400">Ownership</span><Sel testid={`salvage-ownership-${s.id}`} value={s.ownership_status} onChange={(v) => patch(s.id, { ownership_status: v })} options={OWNERSHIP} /></div>
              <div><span className="text-[10px] uppercase tracking-wide text-gray-400">QC Status</span><Sel testid={`salvage-qc-${s.id}`} value={s.qc_status} onChange={(v) => patch(s.id, { qc_status: v })} options={QC} /></div>
            </div>
            {s.ownership_status === "TITANIUM" && s.qc_status === "READY" && !s.gallery_id && <p className="mt-2 text-[11px] font-medium text-emerald-700">Ownership TITANIUM + QC READY auto-creates the Pernah Baru Gallery listing.</p>}
          </div>
        ))}
        {visible.length === 0 && <p className="py-10 text-center text-sm text-gray-400">No salvage records in this view.</p>}
      </div>
    </div>
  );
}
